import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BackButton from '../components/BackButton';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowRight, BookOpen, Star, Tag } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import actualites from '../content/actualites.json';

const linkClass = 'inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950';

export default function Actualites() {
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const categories = ['Tous', ...new Set(actualites.map((article) => article.categorie))];
  const featured = selectedCategory === 'Tous' ? actualites.find((article) => article.featured) : null;
  const articles = actualites.filter((article) => (
    (selectedCategory === 'Tous' || article.categorie === selectedCategory) && article.slug !== featured?.slug
  ));

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="actualites-page">
      <Navigation />
      <main>
        <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-28 pb-16 md:pt-32">
          <div aria-hidden="true" className="absolute -right-32 top-8 h-96 w-96 rounded-full bg-blue-600/15 blur-3xl" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8"><BackButton to="/" label="Retour à l'accueil" className="text-slate-300 hover:text-white" /></div>
            <Badge className="bg-blue-500/15 text-blue-200 border border-blue-400/25 mb-6 px-4 py-2">
              <BookOpen aria-hidden="true" className="w-4 h-4 mr-2" /> Comprendre et agir
            </Badge>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight mb-6">Actualités et ressources</h1>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl leading-relaxed">
              Des repères pour comprendre OBJECTIF SCOP OUTREMER, préparer une demande et participer à la coopération dans les Outre-mer.
            </p>
            <p className="mt-4 text-slate-400 max-w-3xl">
              Retrouvez ici nos guides pratiques sur les services, les parcours et les formes de soutien.
            </p>
          </div>
        </section>

        <section aria-label="Filtrer les ressources" className="py-6 bg-slate-900/50 border-y border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap gap-3">
            {categories.map((category) => (
              <button key={category} type="button" onClick={() => setSelectedCategory(category)}
                aria-pressed={selectedCategory === category}
                className={`px-5 py-2.5 rounded-full font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${selectedCategory === category ? 'bg-amber-400 text-slate-950' : 'bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700'}`}>
                {category}
              </button>
            ))}
          </div>
        </section>

        {featured && (
          <section aria-label="Ressource à découvrir" className="pt-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <article className="rounded-3xl bg-gradient-to-br from-slate-800/80 to-slate-900 border border-slate-700 overflow-hidden">
                <div className="p-7 md:p-12 grid md:grid-cols-[1fr_2fr] gap-8 items-center">
                  <div aria-hidden="true" className={`hidden md:flex aspect-square rounded-2xl bg-gradient-to-br ${featured.gradient} items-center justify-center`}>
                    <BookOpen className="h-24 w-24 text-white/90" strokeWidth={1.25} />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-3 mb-5">
                      <Badge className="bg-amber-500/15 text-amber-200 border border-amber-400/25"><Star aria-hidden="true" className="w-3 h-3 mr-1" /> À découvrir</Badge>
                      <span className="text-sm text-slate-300">{featured.categorie} · Guide pratique</span>
                    </div>
                    <h2 className="text-2xl md:text-4xl font-bold text-white mb-5">{featured.titre}</h2>
                    <p className="text-slate-300 leading-relaxed mb-7">{featured.description}</p>
                    <Link to={`/actualites/${featured.slug}`} className={`${linkClass} bg-amber-400 text-slate-950 hover:bg-amber-300`}>
                      Lire le guide <ArrowRight aria-hidden="true" className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          </section>
        )}

        <section className="py-12 md:py-16" aria-labelledby="ressources-title">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="ressources-title" className="text-2xl font-bold mb-7">{selectedCategory === 'Tous' ? 'Les guides pratiques' : `Ressources : ${selectedCategory}`}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="actualites-grid">
              {articles.map((article) => (
                <article key={article.slug} className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden flex flex-col">
                  <div aria-hidden="true" className={`h-32 bg-gradient-to-br ${article.gradient} flex items-center justify-center`}><BookOpen className="w-12 h-12 text-white/90" strokeWidth={1.5} /></div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-300 mb-4"><Tag aria-hidden="true" className="h-3.5 w-3.5" /> {article.categorie}</span>
                    <h3 className="text-xl font-bold text-white mb-3">{article.titre}</h3>
                    <p className="text-slate-300 text-sm leading-relaxed mb-6">{article.description}</p>
                    <Link to={`/actualites/${article.slug}`} className="inline-flex items-center gap-2 mt-auto text-amber-300 hover:text-amber-200 font-semibold rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300">
                      Lire le guide <span className="sr-only"> : {article.titre}</span><ArrowRight aria-hidden="true" className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-slate-800 bg-slate-900/50 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row gap-6 sm:items-center sm:justify-between">
            <div><h2 className="text-2xl font-bold mb-2">Vous souhaitez préciser votre besoin ?</h2><p className="text-slate-300">Présentez votre activité, votre territoire et le sujet de votre demande.</p></div>
            <Link to="/contact" className={`${linkClass} shrink-0 bg-blue-600 text-white hover:bg-blue-500`}>Contacter O’SCOP <ArrowRight aria-hidden="true" className="h-4 w-4" /></Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
